/**
 * In-place cache patch operations for events.
 *
 * Mirrors `src/lib/cache/questions/operations.ts`'s `updateCollectionQuestion` /
 * `applyQuestionUpdate` pair (the #686/#931 pattern): patch every mounted cache that holds a
 * changed event in place via `setQueriesData`/`setQueryData`, instead of invalidating and
 * forcing a refetch storm across every connected client.
 */

import type { QueryClient } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import type { EventRead } from '@models/api/event';
import { eventKey, isEventListKey, isResolveEventKey } from './queryKeys';
import { asEventCacheItem } from './helpers';
import { updateCollection, patchDetailData, skipUnchanged } from '../collections';

// ── Collection walker (thin wrapper over the shared generic) ────────────────

/**
 * Patches `eventId` in place across whatever cache shape `collection` is, via the shared
 * multi-shape walk. Binds `updateCollection` to `EventRead` + `asEventCacheItem`; see
 * `../collections` for the shapes handled and the same-reference answer for a collection that
 * does not hold the event, which keeps the every-event.updated herd retired (#686/#931) and lets
 * `applyEventUpdate` skip the write. The single-object branch covers a detail /
 * resolver payload or a page envelope already unwrapped one level (useInfiniteSpaceEvents /
 * useInfiniteMyCalendars).
 */
export const updateCollectionEvent = (
  collection: unknown,
  eventId: string,
  updater: (event: EventRead) => EventRead,
): unknown => updateCollection<EventRead>(collection, eventId, updater, asEventCacheItem);

// ── Unified cache writer ────────────────────────────────────────────────────

/**
 * Patches `eventId`'s data in place across every mounted cache that could hold it:
 *  1. Every event LIST cache (`isEventListKey` predicate -- general/featured/space-scoped/
 *     my-calendars feeds), via `updateCollectionEvent`'s shape-aware walk.
 *  2. The uuid detail cache (`eventKey(eventId)`).
 *  3. The slug-resolver cache (`resolveEventKey(slug)`), addressed by PREDICATE rather than a
 *     slug arg the caller may not have (the SSE handler never carries the slug). The
 *     `record.id === eventId` guard is load-bearing: it ensures we only touch THIS event's
 *     resolver entry and never stamp its data onto a different event that happens to have a
 *     mounted resolver query.
 *
 * The two detail caches are patched via the shared `patchDetailData` (`../collections`, #953
 * review) -- see its docstring for the `{ data: EventRead, ... }` envelope shape and no-op
 * semantics. `guard` is how the resolver call below scopes the write to THIS event's entry
 * without a slug arg; the uuid-detail call omits it (the key already pins the event).
 *
 * Only an entry the patch changes is written (`skipUnchanged`). A list that does not hold the
 * event, or another event's resolver entry, keeps its state as it was: its stale mark if it has
 * one, and its freshness clock. A write there would mark fresh an entry this patch knows nothing
 * about.
 *
 * Does NOT touch the attendee roster (`eventAttendeesKey`) -- that cache holds a user list, not
 * an `EventRead`, and is un-patchable from this payload; callers invalidate it separately.
 */
export const applyEventUpdate = (
  queryClient: QueryClient,
  eventId: string,
  updater: (event: EventRead) => EventRead,
) => {
  // Use the isEventListKey predicate so every list prefix is covered
  queryClient.setQueriesData(
    { predicate: query => isEventListKey(query.queryKey) },
    skipUnchanged(old => updateCollectionEvent(old, eventId, updater)),
  );

  queryClient.setQueryData(
    eventKey(eventId),
    skipUnchanged(old => patchDetailData(old, updater, asEventCacheItem)),
  );

  queryClient.setQueriesData(
    { predicate: query => isResolveEventKey(query.queryKey) },
    skipUnchanged(old =>
      patchDetailData(old, updater, asEventCacheItem, record => record.id === eventId),
    ),
  );
};

// ── Cross-key read ──────────────────────────────────────────────────────────

/**
 * The Event as a slug-resolver entry holds it, with when it was read, for a reader keyed by id
 * (`eventKey`) on a screen that read the Event by slug: seed from it instead of asking again.
 * Same `record.id === eventId` guard as applyEventUpdate's resolver branch, so another event's
 * resolver entry is never taken for this one.
 *
 * An invalidated read (a Going's settle has just swept it) still paints, but must not pass for
 * fresh: its age is 0, so a seeded entry is fetched on mount. 0, not undefined: TanStack stamps
 * Date.now() for an undefined `initialDataUpdatedAt`.
 */
export const findResolvedEventRead = (
  queryClient: QueryClient,
  eventId: string,
): { response: AxiosResponse<EventRead>; dataUpdatedAt: number } | undefined => {
  for (const query of queryClient.getQueryCache().findAll({
    predicate: q => isResolveEventKey(q.queryKey),
  })) {
    const response = query.state.data as AxiosResponse<EventRead> | undefined;
    if (response?.data.id === eventId) {
      return {
        response,
        dataUpdatedAt: query.state.isInvalidated ? 0 : query.state.dataUpdatedAt,
      };
    }
  }
  return undefined;
};
